import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { StatusToneChip } from 'src/components/partner-driver/shared/StatusToneChip'
import type { PartnerDriverSearchRow } from 'src/interfaces/partnerDriver'
import { formatDriverName } from 'src/utils/partnerDriver/driverName'
import { formatCents, formatPercentage, toPercentageNumber } from 'src/utils/partnerDriver/money'
import { partnerDriverTableHeaderTitles } from './constants'
import { PartnerDriversTableFields, StatusTone } from './enums'

const renderText = (value: string | null | undefined, isMonospace = false) => {
  const text = (value ?? '').trim()

  if (text === '') {
    // A dash for a missing optional value is a cosmetic default, not fabricated content.
    return (
      <Typography variant="body2" css={tw`text-shades-500`}>
        —
      </Typography>
    )
  }

  return (
    <Typography variant="body2" css={[tw`truncate`, isMonospace ? tw`font-mono` : null]}>
      {text}
    </Typography>
  )
}

/**
 * No closures, so a module constant (unlike `buildMarketColumns`). v8 renders cells in text mode;
 * every element-returning column sets `display: 'flex'` so it centres vertically.
 */
export const partnerDriverColumns: Array<GridColDef<PartnerDriverSearchRow>> = [
  {
    field: PartnerDriversTableFields.name,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.name],
    flex: 2,
    minWidth: 180,
    hideable: false,
    display: 'flex',
    valueGetter: (_value: unknown, row: PartnerDriverSearchRow) => {
      return formatDriverName(row.firstName, row.lastName)
    },
    renderCell: (params: GridRenderCellParams<PartnerDriverSearchRow>) => {
      return renderText(formatDriverName(params.row.firstName, params.row.lastName))
    },
  },
  {
    field: PartnerDriversTableFields.email,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.email],
    flex: 2,
    minWidth: 200,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerDriverSearchRow>) => {
      return renderText(params.row.email)
    },
  },
  {
    field: PartnerDriversTableFields.phone,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.phone],
    width: 150,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerDriverSearchRow>) => {
      return renderText(params.row.phone)
    },
  },
  {
    field: PartnerDriversTableFields.partnerName,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.partnerName],
    flex: 1,
    minWidth: 150,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerDriverSearchRow>) => {
      return renderText(params.row.partnerName)
    },
  },
  {
    field: PartnerDriversTableFields.isActive,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.isActive],
    width: 110,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerDriverSearchRow>) => {
      if (params.row.isActive) {
        return <StatusToneChip tone={StatusTone.Success} text="Active" />
      }

      return <StatusToneChip tone={StatusTone.Neutral} text="Inactive" />
    },
  },
  {
    field: PartnerDriversTableFields.reimbursementAmount,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.reimbursementAmount],
    width: 140,
    type: 'number',
    valueFormatter: (value: unknown) => {
      return formatCents(typeof value === 'number' ? value : null)
    },
  },
  {
    field: PartnerDriversTableFields.reimbursementPercentage,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.reimbursementPercentage],
    width: 140,
    type: 'number',
    // pg returns decimal(4,2) as a string; sort on the number.
    valueGetter: (value: unknown) => {
      return toPercentageNumber(value as number | string | null)
    },
    valueFormatter: (value: unknown) => {
      return formatPercentage(value as number | null)
    },
  },
  {
    field: PartnerDriversTableFields.pendingChecks,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.pendingChecks],
    width: 130,
    type: 'number',
    valueGetter: (_value: unknown, row: PartnerDriverSearchRow) => {
      return row.backgroundCheckReports?.length ?? 0
    },
  },
  {
    field: PartnerDriversTableFields.otpCode,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.otpCode],
    width: 110,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerDriverSearchRow>) => {
      return renderText(params.row.otpCode, true)
    },
  },
  {
    field: PartnerDriversTableFields.collectDataTemplateId,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.collectDataTemplateId],
    width: 140,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerDriverSearchRow>) => {
      return renderText(params.row.collectDataTemplateId, true)
    },
  },
  {
    field: PartnerDriversTableFields.id,
    headerName: partnerDriverTableHeaderTitles[PartnerDriversTableFields.id],
    width: 140,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerDriverSearchRow>) => {
      return renderText(params.row.id, true)
    },
  },
]
